import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ElementRef,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

/**
 * inputs:
 * -------
 * input label?: string
 * input placeholder?: string
 * input mandatory?: boolean
 * input formName?: string
 * input type radio|checkbox|string
 * input isMultiselect?: boolean
 * input maxSelect?: number
 * input minSelect?: number
 * input defaultSelected?: any
 * input showSearch?: boolean
 * input noteText?: string
 * input isDisabled?: boolean
 * input setFocus?: boolean
 * input errorMessage?: string
 * input showError?: boolean
 * ---------------------------
 * events:
 * -------
 * output focused
 * output blured
 * output clicked
 * output changed
 * output validationError
 */

@Component({
  selector: 'ops-input-with-list-items',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './ops-input-with-list-items.component.html',
  styleUrls: ['./ops-input-with-list-items.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OpsInputWithListItemsComponent implements OnInit, OnDestroy {
  private _minSelect = 0;
  private _maxSelect = 5;

  @Input() label?: string;
  @Input() placeholder? = '';
  @Input() mandatory?: boolean = false;
  @Input() formName?: string;
  @Input() trackByKey?: string;
  @Input() set maxSelect(val: number) {
    this._maxSelect = val;
  }
  get maxSelect() {
    return this._maxSelect;
  }

  get maxApplied(): boolean {
    return this.maxSelect <= this.selectedItems.length;
  }

  @Input() set minSelect(val: number) {
    this._minSelect = val;
  }
  get minSelect() {
    return this._minSelect;
  }

  get placeholderText(): string {
    if (this.maxApplied) {
      return 'OPS_INPUT_WITH_LIST_ITEMS.MAXIMUM_REACHED';
    }
    return this.placeholder;
  }

  private _defaultSelected: any = [];

  private _isSingleOption = false;

  private _isPreview = false;

  @Input() set isPreview(val: boolean) {
    this._isPreview = val;

    this.selectedValue = '';
    this.searchQuery = '';
  }

  get isPreview() {
    return this._isPreview;
  }

  @Input() showSearch?: boolean = false;
  @Input() showLimitNote?: boolean;
  @Input() noteText?: string;
  @Input() isDisabled?: boolean = false;
  @Input() setFocus?: boolean = false;
  @Input() errorMessage?: string;
  @Input() showError?: boolean = false;

  @Output() focused = new EventEmitter<void>();
  @Output() blured = new EventEmitter<void>();
  @Output() clicked = new EventEmitter<void>();
  @Output() changed = new EventEmitter<any[]>();
  @Output() validationError = new EventEmitter<string>();

  selectedItems: any[] = [];
  selectedValue?: string;
  searchQuery = '';
  mappedOptions: { key: any; value: string; isDisabled?: boolean }[] = [];
  isDropdownOpen = false;
  highlightedIndex: number | null = null;
  closeTimeout: any = null;

  mouseLeft = false;

  prevElement?: any;

  @Input()
  set defaultSelected(value: any) {
    // If the input value is provided, use it; otherwise, use the default value (an empty array)

    if (!!value !== true) {
      this._defaultSelected = [];
    } else {
      this._defaultSelected = value;
    }

    this.searchQuery = '';

    this.mappedOptions = this.mapOptions(value);
    this.selectedItems = this.mapDefaultSelected();

    // this.updateSelections();

    if (this.selectedItems.length) {
      this.highlightedIndex = this.filteredItems.indexOf(this.selectedItems[0]);
    }
  }

  get defaultSelected() {
    return this._defaultSelected.map((ds) => ds.value);
  }

  constructor(private elementRef: ElementRef) {}

  mapOptions(values: Array<string>) {
    if (values.length === 0) return [];

    return (values as string[]).map((item, index) => ({
      key: index,
      value: item,
    }));
  }

  ngOnDestroy(): void {
    this.isDropdownOpen = false;
    clearTimeout(this.closeTimeout);
  }

  ngOnInit() {
    this.validateInputs();
    this.selectedItems = this.mapDefaultSelected();
  }

  updateSelections() {
    // Clear the selectedItems list
    this.selectedItems = [];
    this.selectedValue = '';

    if (typeof this._defaultSelected !== 'object') {
      this._defaultSelected = [];
    }
    // Iterate through the _defaultSelected values and compare them with mappedOptions
    this._defaultSelected.forEach((defaultSelectedItem) => {
      const matchedOption = this.mappedOptions.find(
        (option) =>
          option.value === defaultSelectedItem.value ||
          option.key === defaultSelectedItem.key ||
          option.value === defaultSelectedItem
      );

      if (matchedOption) {
        // If a matching option is found, add it to the selectedItems list
        this.selectedItems.push(matchedOption);
      }
    });
  }

  // Validate inputs
  validateInputs() {
    // Validate defaultSelected
    if (this._defaultSelected && !Array.isArray(this._defaultSelected)) {
      throw new Error("The 'defaultSelected' input must be an array.");
    }
  }

  // Map default selected options array to object array
  mapDefaultSelected() {
    if (this._defaultSelected.length === 0) return [];

    if (typeof this._defaultSelected[0] === 'string') {
      return (this._defaultSelected as string[]).map((item, index) => {
        if (this.mappedOptions.length) {
          const keyIndex = this.mappedOptions.find(
            (mo) => mo?.value === item || mo?.key === item
          ).key;
          return {
            key: keyIndex,
            value: item,
          };
        }
      });
    }

    return (this._defaultSelected as { key: any; value: string }[]).map(
      (item, index) => {
        const keyIndex = this.mappedOptions.find(
          (mo) => mo.value === item.value || mo.key === item.value
        )?.key;

        return {
          key: keyIndex,
          value: item,
        };
      }
    );
  }

  onEnterPressed(event: KeyboardEvent) {
    if (
      this.searchQuery.length > 0 &&
      this.maxSelect > this.selectedItems.length
    ) {
      const haveMatch = this.mappedOptions.find(
        (mO) => mO.value === this.searchQuery
      );
      if (!haveMatch) {
        this.mappedOptions.push({
          key: this.mappedOptions.length,
          value: this.searchQuery,
          isDisabled: false,
        });

        this.selectedItems.push({
          key: this.mappedOptions.length,
          value: this.searchQuery,
        });

        this.searchQuery = '';

        this.emitSelectedItems();
      }
    }
  }

  isElementFullyVisibleInContainer(
    element: HTMLElement,
    container: HTMLElement
  ) {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const isBottomVisible =
      containerRect.bottom + container.scrollTop >
      elementRect.bottom + 200 + element.scrollHeight;

    return isBottomVisible;
  }

  get dropdownOrientation(): boolean {
    if (this.isDropdownOpen === false) {
      return false;
    }

    const docContainer = document.getElementById('holding-container');
    if (docContainer) {
      const isVisible = this.isElementFullyVisibleInContainer(
        this.elementRef.nativeElement,
        docContainer
      );

      if (!isVisible) {
        return true;
      }
    }
    return false;
    // if(this.elementRef.nativeElement.offsetTop())
  }

  get isDropdownShowing(): boolean {
    return this.isDropdownOpen;
  }

  emitSelectedItems() {
    const selectedItemsMap = this.selectedItems.map((si) => si.value);
    this.changed.emit(selectedItemsMap);
  }

  // Handle removing items from selected list
  removeSelectedItem(item: { key: any; value: string }) {
    if (!this.isDisabled && !this.isPreview) {
      this.selectedItems = this.selectedItems.filter((i) => i.key !== item.key);
      this.emitSelectedItems();
      this.elementRef.nativeElement.focus();
    }
  }

  // Handle selecting items
  onSelectItem(item: { key: any; value: string }) {
    if (this.isDisabled) return;

    if (this.selectedItems.filter((si) => si.key === item.key)?.length > 0) {
      this.removeSelectedItem(item);
    } else {
      if (this.maxSelect && this.selectedItems.length >= this.maxSelect) {
        this.validationError.emit(
          `You can only select up to ${this.maxSelect} items`
        );
        return;
      }
      this.selectedItems.push(item);
    }

    this.emitSelectedItems();
    this.validateSelection();

    this.elementRef.nativeElement.focus();
  }

  onSearchChange(event: Event) {
    this.searchQuery = (event.target as HTMLInputElement).value;
    this.highlightedIndex = null; // reset highlight on search
    this.elementRef.nativeElement.focus();
  }

  validateSelection() {
    if (this.mandatory && this.selectedItems.length < this.minSelect) {
      this.showError = true;
      this.errorMessage = `Please select at least ${this.minSelect} items`;
      this.validationError.emit(this.errorMessage);
    } else {
      this.showError = false;
    }
    this.elementRef.nativeElement.focus();
  }

  get filteredItems() {
    if (this.searchQuery === this.selectedValue) {
      return this.mappedOptions;
    }
    return this.mappedOptions.filter((item) =>
      item?.value
        ?.toLowerCase()
        ?.includes(this.searchQuery?.toLowerCase() ?? '')
    );
  }

  isSelected(item: { key: any; value: string }) {
    return this.selectedItems.filter((si) => si.key === item.key)?.length > 0;
  }

  // Set styles based on type and selection state
  getItemClass(item: { key: any; value: string }) {
    return {
      selected: this.isSelected(item),
      disabled: this.isDisabled,
      highlighted: this.filteredItems.indexOf(item) === this.highlightedIndex,
    };
  }
}
